class PromotionPopup extends HTMLElement {
  constructor() {
    super();

    this.expiresDays = this.getAttribute('data-expire') || 1;
    this.timeToShow = parseInt(this.getAttribute('data-delay')) || 0;
    this._storage = this.getBestStorage();

    const popupId = this.id || 'global';
    this.dismissKey = `_halo_promotion_popup_dismiss_${popupId}`;

    this.overlay = this.querySelector('.drawer__overlay') || this.querySelector('[id^="Drawer-Overlay-"]');
    this.content = this.querySelector('.popup__inner');
    this.hasOpened = false;
    this.openTimer = null;
    this.idleTimer = null;
    this.cartAddTimer = null;
    this.cleanupHandlers = [];
    this.createdAt = Date.now();

    this.querySelector('.popup__close')?.addEventListener('click', this.dismiss.bind(this));
    this.overlay?.addEventListener('click', this.dismiss.bind(this));

    if (Shopify.designMode) {
      document.addEventListener('shopify:section:select', (e) => {
        if (!e?.target?.matches?.('.section-promotion-popup')) return;
        const selectedSectionId = e?.detail?.sectionId;
        const mySectionId = (this.id || '').replace('Promotion-popup-', '');
        if (selectedSectionId && mySectionId && selectedSectionId !== mySectionId) return;
        this.setOpenPopup(0);
      });

      document.addEventListener('shopify:section:deselect', (e) => {
        if (!e?.target?.matches?.('.section-promotion-popup')) return;
        const deselectedSectionId = e?.detail?.sectionId;
        const mySectionId = (this.id || '').replace('Promotion-popup-', '');
        if (deselectedSectionId && mySectionId && deselectedSectionId !== mySectionId) return;
        this.close({ persist: false });
      });
    }
  }

  connectedCallback() {
    try { this.load(); } catch (_) { this.setOpenPopup(this.timeToShow); }
  }

  disconnectedCallback() {
    this.clearScheduledOpen();
    this.cleanupBehaviorTriggers();
    this.successObserver?.disconnect?.();
  }

  load() {
    this.observeEmailSuccess();

    const hasSuccess = this.querySelector('.email-signup__message--success');
    const hasMessage = this.querySelector('.email-signup__message');

    if (hasSuccess) {
      if (hasMessage) this.classList.add('active');
      if (!Shopify.designMode) this.setOpenPopup(0);
      return;
    }

    if (this.getStore(this.dismissKey)) return;

    if (hasMessage) this.classList.add('active');
    if (!Shopify.designMode) this.setupBehaviorTriggers();
  }

  observeEmailSuccess() {
    const target = this.querySelector('form') || this;
    this.successObserver = new MutationObserver((mutations) => {
      mutations.forEach((mutation) => {
        mutation.addedNodes.forEach((node) => {
          if (node.nodeType !== 1) return;
          if (node.classList.contains('email-signup__message--success')) {
            this.classList.add('active');
            this.setOpenPopup(0);
          }
        });
      });
    });
    this.successObserver.observe(target, { childList: true, subtree: true });
  }

  setOpenPopup(timeToShow) {
    this.requestOpen('scheduled', timeToShow);
  }

  setupBehaviorTriggers() {
    this.cleanupBehaviorTriggers();
    this.setupDesktopExitIntent();
    this.setupCartAddIntent();

    if (this.isProductPage()) {
      this.setupPdpScrollIntent();
      this.requestOpen('pdp-fallback', Math.max(this.timeToShow, 25000));
      return;
    }

    this.requestOpen('fallback', this.timeToShow);
  }

  setupDesktopExitIntent() {
    if (!window.matchMedia?.('(hover: hover) and (pointer: fine)').matches) return;

    const onMouseLeave = (event) => {
      if (event.clientY > 16) return;
      document.removeEventListener('mouseleave', onMouseLeave);
      this.requestOpen('exit-intent', 0);
    };

    document.addEventListener('mouseleave', onMouseLeave);
    this.cleanupHandlers.push(() => document.removeEventListener('mouseleave', onMouseLeave));
  }

  setupPdpScrollIntent() {
    const onScroll = () => {
      const doc = document.documentElement;
      const scrollable = Math.max(doc.scrollHeight - window.innerHeight, 1);
      const depth = (window.scrollY || doc.scrollTop || 0) / scrollable;

      if (depth < 0.5) return;
      window.removeEventListener('scroll', onScroll);
      const elapsed = Date.now() - this.createdAt;
      this.scheduleIdleOpen('pdp-scroll', Math.max(2500, 10000 - elapsed));
    };

    window.addEventListener('scroll', onScroll, { passive: true });
    this.cleanupHandlers.push(() => window.removeEventListener('scroll', onScroll));
    onScroll();
  }

  setupCartAddIntent() {
    const onCartAddSuccess = () => {
      window.clearTimeout(this.cartAddTimer);
      this.cartAddTimer = window.setTimeout(() => {
        if (this.isCartOrCheckoutPage() || this.isCommerceOverlayActive()) return;
        this.scheduleIdleOpen('cart-add', 2500);
      }, 2000);
    };

    document.addEventListener('evolova:cart-add-success', onCartAddSuccess);
    this.cleanupHandlers.push(() => document.removeEventListener('evolova:cart-add-success', onCartAddSuccess));
  }

  scheduleIdleOpen(reason, delay) {
    window.clearTimeout(this.idleTimer);
    this.idleTimer = window.setTimeout(() => {
      if ('requestIdleCallback' in window) {
        window.requestIdleCallback(() => this.requestOpen(reason, 0), { timeout: 2000 });
        return;
      }
      this.requestOpen(reason, 0);
    }, delay);
  }

  requestOpen(reason, delay) {
    window.clearTimeout(this.openTimer);
    this.openTimer = window.setTimeout(() => this.openNow(reason), Math.max(parseInt(delay, 10) || 0, 0));
  }

  openNow() {
    if (this.shouldBlockOpen()) return;

    this.hasOpened = true;
    this.cleanupBehaviorTriggers();
    this.activeElement = document.activeElement;
    this.setAttribute('open', 'true');
    this.animateOpen();
    document.body.classList.add('overflow-hidden');
    document.documentElement.setAttribute('scroll-lock', '');

    this.addEventListener('transitionend', () => {
      const containerToTrapFocusOn = this.content;
      const focusElement = this.querySelector('.field__input');
      trapFocus(containerToTrapFocusOn, focusElement);
    }, { once: true });
  }

  shouldBlockOpen() {
    if (this.hasAttribute('open')) return true;
    if (Shopify.designMode) return false;
    if (this.hasOpened || this.getStore(this.dismissKey)) return true;
    if (this.isCartOrCheckoutPage()) return true;
    if (this.isCommerceOverlayActive()) return true;
    if (document.visibilityState === 'hidden') return true;
    return false;
  }

  isProductPage() {
    return document.body.classList.contains('template-product') ||
      document.querySelector('main-product, product-info, .main-product-information, .productView') !== null;
  }

  isCartOrCheckoutPage() {
    return /^\/(cart|checkout|checkouts)(\/|$)/.test(window.location.pathname);
  }

  isCommerceOverlayActive() {
    return document.querySelector('cart-drawer[open], cart-drawer.active, .cart-drawer[open], .cart-drawer.active') !== null ||
      document.body.classList.contains('overflow-hidden') ||
      document.documentElement.hasAttribute('scroll-lock');
  }

  clearScheduledOpen() {
    window.clearTimeout(this.openTimer);
    window.clearTimeout(this.idleTimer);
    window.clearTimeout(this.cartAddTimer);
    this.openTimer = null;
    this.idleTimer = null;
    this.cartAddTimer = null;
  }

  cleanupBehaviorTriggers() {
    this.cleanupHandlers.splice(0).forEach((cleanup) => {
      try { cleanup(); } catch (_) {}
    });
  }

  close({ persist } = { persist: true }) {
    this.clearScheduledOpen();
    this.cleanupBehaviorTriggers();
    if (persist) this.setStoreDays(this.dismissKey, '1', this.expiresDays);
    this.animateClose();
    setTimeout(() => {
      this.removeAttribute('open');
      document.body.classList.remove('overflow-hidden');
      document.documentElement.removeAttribute('scroll-lock');
      removeTrapFocus(this.activeElement);
    }, 100);
  }

  animateOpen() {
    Motion.timeline([
      [this.content, { opacity: [0, 1], transform: ['translate(-50%, -40%)', 'translate(-50%, -50%)'] }, { duration: 0.3, at: '-0.15' }]
    ]).finished;
  }

  animateClose() {
    Motion.timeline([
      [this.content, { opacity: [1, 0], transform: ['translate(-50%, -50%)', 'translate(-50%, -40%)'] }, { duration: 0.2 }]
    ]).finished;
  }

  tempClose() {
    this.animateClose();
    setTimeout(() => {
      this.removeAttribute('open');
      document.body.classList.remove('overflow-hidden');
      document.documentElement.removeAttribute('scroll-lock');
      removeTrapFocus(this.activeElement);
    }, 100);
  }

  dismiss() {
    this.close({ persist: true });
  }

  setActiveElement(element) {
    this.activeElement = element;
  }

  setStoreDays(name, value, days) {
    var d = new Date();
    d.setTime(d.getTime() + 24 * 60 * 60 * 1000 * days);
    this.setStoreUntil(name, value, d);
  }

  setStoreUntil(name, value, date) {
    try {
      if (!this._storage) return;
      var payload = { v: value, exp: date.getTime() };
      this._storage.setItem(name, JSON.stringify(payload));
    } catch (_) {}
  }

  getStore(name) {
    try {
      if (!this._storage) return null;
      var raw = this._storage.getItem(name);
      if (!raw) return null;
      var payload = JSON.parse(raw);
      if (!payload || typeof payload.exp !== 'number') return null;
      if (Date.now() > payload.exp) {
        this._storage.removeItem(name);
        return null;
      }
      return payload.v;
    } catch (_) { return null; }
  }

  getBestStorage() {
    try {
      var k = '__pp_test__';
      window.localStorage.setItem(k, '1');
      window.localStorage.removeItem(k);
      return window.localStorage;
    } catch (_) {}
    try {
      var k2 = '__pp_test__';
      window.sessionStorage.setItem(k2, '1');
      window.sessionStorage.removeItem(k2);
      return window.sessionStorage;
    } catch (_) {}
    return null;
  }
}
if (!customElements.get('promotion-popup')) customElements.define('promotion-popup', PromotionPopup);
